import React, { useEffect, useRef, useState } from "react";
import { Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { gameUrl, getGameOrigins } from "./gameService";

/**
 * The game in a frame - used for the practice round and the real game. Only messages from the game's own server
 * (and on the list of allowed game servers) count, and only with this round and mode. Games from before the
 * protocol (a bare { score }) are accepted in the real round. If the game doesn't load within 15 seconds, the
 * player can continue without it (score 0) instead of being stuck.
 */
interface Props {
  gameCode: string;
  mode: "real" | "practice";
  seconds: number;
  round: string;
  onStarted?: () => void;
  onScore: (score: number) => void;
  onGiveUp?: () => void;
}

const LOAD_TIMEOUT_MS = 15000;
const primary = { backgroundColor: "#EB6608", color: "#FFFFFF", width: "100%", textTransform: "none" as const, height: "3rem", borderRadius: "10px" };

export const GameFrame = ({ gameCode, mode, seconds, round, onStarted, onScore, onGiveUp }: Props) => {
  const [t] = useTranslation("global");
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const done = useRef(false);
  const loadedRef = useRef(false);
  const src = gameUrl(gameCode, { mode, seconds, round, lang: "da" });

  useEffect(() => {
    done.current = false;
    loadedRef.current = false;
    setLoaded(false);
    setFailed(false);
    const timer = setTimeout(() => setFailed((already) => already || !loadedRef.current), LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [src]);

  useEffect(() => {
    let origin = "";
    try {
      origin = new URL(gameCode, window.location.href).origin;
    } catch {
      origin = "";
    }
    let allowed: string[] = [];
    getGameOrigins().then((list) => (allowed = list));
    const handle = (event: MessageEvent) => {
      if (!origin || event.origin !== origin || (allowed.length && !allowed.includes(origin))) return;
      const data = event.data || {};
      if (data.type === "breefer:ready") {
        loadedRef.current = true;
        setLoaded(true);
        return;
      }
      if (data.type === "breefer:started") {
        if (data.round === round) onStarted?.();
        return;
      }
      const legacy = data.type === undefined && mode === "real";
      const ours = data.type === "breefer:score" && data.round === round && data.mode === mode;
      if (!ours && !legacy) return;
      const score = Number(data.score);
      if (done.current || !Number.isFinite(score) || score < 0) return;
      done.current = true;
      onScore(Math.round(score));
    };
    window.addEventListener("message", handle);
    return () => window.removeEventListener("message", handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameCode, mode, round]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <iframe
        src={src}
        title={t("gamePlay.frameTitle")}
        width="100%"
        height="100%"
        style={{ border: "none", display: "block" }}
        allow="autoplay"
        sandbox="allow-scripts allow-same-origin"
        // Note: no onLoad here. An error page also "loads", and then the way out below would never appear.
        // `loadedRef` is set when the game itself says hello (breefer:ready).
      />
      {failed && !loaded && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.95)", display: "flex", flexDirection: "column", justifyContent: "center", padding: "1.5rem", fontFamily: "Roboto, Arial, sans-serif", textAlign: "center" }}>
          <p>{t("gamePlay.loadFailed")}</p>
          {onGiveUp && (
            <Button variant="contained" style={primary} onClick={onGiveUp}>
              {t("gamePlay.continueWithout")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
